import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Tag,
  Table,
  Space,
  Typography,
  DatePicker,
  Modal,
  Form,
  InputNumber,
  message,
  Tooltip,
  Popconfirm,
  Progress,
  Statistic,
  Skeleton
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  StopOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { promotionsService, Promotion, PromotionFilters } from '../services/promotionsService';
import { useAuth } from '../contexts/AuthContext';
import { exportToCSV } from '../utils/exportUtils';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PromotionsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [filters, setFilters] = useState<PromotionFilters>({
    page: 1,
    limit: 10
  });
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    active: 0,
    upcoming: 0,
    expired: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  // Charger les promotions
  const loadPromotions = useCallback(async (page = 1, pageSize = 10, customFilters?: PromotionFilters) => {
    try {
      setLoading(true);
      setError(null);
      
      const activeFilters = customFilters || filters;
      const response = await promotionsService.getPromotions({
        ...activeFilters,
        page,
        limit: pageSize,
      });
      
      // Le service retourne une structure avec response.data.promotions
      const promotionsData = response?.data?.promotions || [];
      setPromotions(promotionsData);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize,
        total: response?.data?.total || 0,
      }));

      if (response?.data?.stats) {
        setGlobalStats(response.data.stats);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des promotions:', error);
      setError(error.message || 'Erreur lors du chargement des promotions');
      setPromotions([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadPromotions(1, pagination.pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleCreate = () => {
    setSelectedPromotion(null);
    setEditMode(false);
    form.resetFields();
    setOpenDialog(true);
  };

  const handleEdit = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setEditMode(true);
    form.setFieldsValue({
      ...promotion,
      start_date: dayjs(promotion.start_date),
      end_date: dayjs(promotion.end_date),
    });
    setOpenDialog(true);
  };

  const handleView = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setOpenViewDialog(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await promotionsService.deletePromotion(id);
      message.success('Promotion supprimée avec succès');
      loadPromotions();
    } catch (error) {
      message.error('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        start_date: values.start_date.format('YYYY-MM-DD'),
        end_date: values.end_date.format('YYYY-MM-DD'),
      };

      if (editMode && selectedPromotion) {
        await promotionsService.updatePromotion(selectedPromotion.id, data);
        message.success('Promotion modifiée avec succès');
      } else {
        await promotionsService.createPromotion(data);
        message.success('Promotion créée avec succès');
      }
      
      setOpenDialog(false);
      loadPromotions();
    } catch (error) {
      message.error('Erreur lors de la sauvegarde');
    }
  };

  const getStatusColor = (promotion: Promotion) => {
    const now = dayjs();
    const start = dayjs(promotion.start_date);
    const end = dayjs(promotion.end_date);

    if (promotion.status === 'cancelled') return 'error';
    if (now.isBefore(start)) return 'warning';
    if (now.isAfter(end)) return 'default';
    return 'success';
  };

  const getStatusText = (promotion: Promotion) => {
    const now = dayjs();
    const start = dayjs(promotion.start_date);
    const end = dayjs(promotion.end_date);

    if (promotion.status === 'cancelled') return 'Annulée';
    if (now.isBefore(start)) return 'À venir';
    if (now.isAfter(end)) return 'Expirée';
    return 'Active';
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'percentage': return 'blue';
      case 'fixed_amount':
      case 'fixed': return 'green';
      case 'free_service': return 'purple';
      case 'bundle': return 'orange';
      case 'FGA1':
      case 'fga1': return 'cyan';
      case 'FGA2':
      case 'fga2': return 'gold';
      case '3splits_50%':
      case 'FGA3':
      case 'fga3': return 'magenta';
      case 'annual_semestriel':
      case 'annuel_semestriel': return 'volcano';
      case 'quarterly':
      case 'trimestriel': return 'purple';
      default: return 'default';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'percentage': return 'Pourcentage';
      case 'fixed_amount':
      case 'fixed': return 'Montant fixe';
      case 'free_service': return 'Service gratuit';
      case 'bundle': return 'Bundle';
      case 'FGA1':
      case 'fga1': return 'FGA1 (À la demande)';
      case 'FGA2':
      case 'fga2': return 'FGA2 (Confort à la demande)';
      case '3splits_50%':
      case 'FGA3':
      case 'fga3': return '3splits_50% (Abonnement annuel ≥ 3 splits)';
      case 'annual_semestriel':
      case 'annuel_semestriel': return 'Annuel-Semestriel (Offres annuelles & semestrielles)';
      case 'quarterly':
      case 'trimestriel': return 'Annuel-Trimestriel (Offres annuelles trimestrielles)';
      default: return type;
    }
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>
          {getTypeText(type)}
        </Tag>
      ),
    },
    {
      title: 'Valeur',
      dataIndex: 'value',
      key: 'value',
      render: (value: number, record: Promotion) => {
        if (record.type === 'percentage' || record.type === '3splits_50%' || record.type === 'FGA3' || record.type === 'fga3') {
          return `${value}%`;
        }
        if (record.type === 'annual_semestriel' || record.type === 'annuel_semestriel') {
          return value <= 100 ? `${value}%` : `${value} FCFA`;
        }
        if (record.type === 'quarterly' || record.type === 'trimestriel') {
          return value <= 100 ? `${value}%` : `${value} FCFA`;
        }
        if (record.type === 'fixed_amount' || record.type === 'fixed' || record.type === 'FGA1' || record.type === 'fga1' || record.type === 'FGA2' || record.type === 'fga2') {
          return `${value} FCFA`;
        }
        return `${value} FCFA`;
      },
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Période',
      key: 'period',
      render: (record: Promotion) => (
        <div>
          <div>{dayjs(record.start_date).format('DD/MM/YYYY')}</div>
          <div>{dayjs(record.end_date).format('DD/MM/YYYY')}</div>
        </div>
      ),
    },
    {
      title: 'Statut',
      key: 'status',
      render: (record: Promotion) => (
        <Tag color={getStatusColor(record)}>
          {getStatusText(record)}
        </Tag>
      ),
    },
    {
      title: 'Utilisation',
      key: 'usage',
      render: (record: Promotion) => {
        if (!record.usage_limit) return '-';
        const percentage = ((record.used_count || 0) / record.usage_limit) * 100;
        return (
          <div>
            <Progress percent={percentage} size="small" />
            <span style={{ fontSize: '12px' }}>
              {record.used_count || 0} / {record.usage_limit}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Promotion) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {isAdmin && (
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer cette promotion ?"
              onConfirm={() => handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Tooltip title="Supprimer">
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Statistiques
  const stats = globalStats.total > 0 ? globalStats : {
    total: pagination.total || promotions?.length || 0,
    active: promotions?.filter(p => getStatusText(p) === 'Active').length || 0,
    upcoming: promotions?.filter(p => getStatusText(p) === 'À venir').length || 0,
    expired: promotions?.filter(p => getStatusText(p) === 'Expirée').length || 0,
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    try {
      setExporting(true);
      const rows = (promotions || []).map(p => {
        const usageRate = p.usage_limit ? `${Math.round(((p.used_count || 0) / p.usage_limit) * 100)}%` : 'Illimité';
        return {
          id: p.id,
          nom: p.name,
          code: p.code,
          type: getTypeText(p.type),
          valeur: (p.type === 'percentage' || p.type === '3splits_50%' || p.type === 'FGA3' || p.type === 'fga3') ? `${p.value}%` : (p.type === 'annual_semestriel' || p.type === 'annuel_semestriel' || p.type === 'quarterly' || p.type === 'trimestriel') ? (p.value <= 100 ? `${p.value}%` : `${p.value} FCFA`) : `${p.value} FCFA`,
          utilisations: p.used_count || 0,
          limite_utilisation: p.usage_limit || 'Illimité',
          taux_efficience: usageRate,
          date_debut: p.start_date ? dayjs(p.start_date).format('DD/MM/YYYY') : '',
          date_fin: p.end_date ? dayjs(p.end_date).format('DD/MM/YYYY') : '',
          statut: getStatusText(p),
        };
      });

      const headers = [
        { header: 'ID', key: 'id' },
        { header: 'Nom Promotion', key: 'nom' },
        { header: 'Code Promo', key: 'code' },
        { header: 'Type', key: 'type' },
        { header: 'Valeur', key: 'valeur' },
        { header: 'Nombre d Utilisations', key: 'utilisations' },
        { header: 'Limite Utilisations', key: 'limite_utilisation' },
        { header: 'Efficience (Taux)', key: 'taux_efficience' },
        { header: 'Date Début', key: 'date_debut' },
        { header: 'Date Fin', key: 'date_fin' },
        { header: 'Statut', key: 'statut' },
      ];

      exportToCSV(rows, headers, `promotions_efficience_${new Date().toISOString().slice(0, 10)}.csv`);
      message.success('Export des promotions généré avec succès');
    } catch (err) {
      console.error('Erreur export promotions:', err);
      message.error("Erreur lors de l'export des promotions");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={3} style={{ margin: 0, color: '#0A543D' }}>
            <GiftOutlined style={{ marginRight: 10 }} />
            Gestion des Promotions
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              style={{ borderRadius: 8, height: 40, fontWeight: 600 }}
            >
              Exporter
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadPromotions(pagination.current, pagination.pageSize)}
              loading={loading}
              style={{ borderRadius: 8, height: 40, fontWeight: 600 }}
            >
              Actualiser
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              style={{ backgroundColor: '#0A543D', borderColor: '#0A543D', borderRadius: 8, height: 40, fontWeight: 600 }}
            >
              Nouvelle Promotion
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)' }}>
            {loading && globalStats.total === 0 ? <Skeleton active paragraph={{ rows: 1 }} /> : (
              <Statistic
                title={<span style={{ color: '#475569', fontWeight: 600 }}>Total</span>}
                value={stats.total}
                prefix={<GiftOutlined style={{ color: '#0A543D' }} />}
                valueStyle={{ color: '#0F172A', fontWeight: 700 }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)' }}>
            {loading && globalStats.total === 0 ? <Skeleton active paragraph={{ rows: 1 }} /> : (
              <Statistic
                title={<span style={{ color: '#475569', fontWeight: 600 }}>Actives</span>}
                value={stats.active}
                prefix={<CheckCircleOutlined style={{ color: '#15803D' }} />}
                valueStyle={{ color: '#15803D', fontWeight: 700 }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)' }}>
            {loading && globalStats.total === 0 ? <Skeleton active paragraph={{ rows: 1 }} /> : (
              <Statistic
                title={<span style={{ color: '#475569', fontWeight: 600 }}>À venir</span>}
                value={stats.upcoming}
                prefix={<ClockCircleOutlined style={{ color: '#D97706' }} />}
                valueStyle={{ color: '#D97706', fontWeight: 700 }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)' }}>
            {loading && globalStats.total === 0 ? <Skeleton active paragraph={{ rows: 1 }} /> : (
              <Statistic
                title={<span style={{ color: '#475569', fontWeight: 600 }}>Expirées</span>}
                value={stats.expired}
                prefix={<StopOutlined style={{ color: '#DC2626' }} />}
                valueStyle={{ color: '#DC2626', fontWeight: 700 }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Input.Search
              placeholder="Rechercher par nom ou code..."
              onSearch={(value) => setFilters(prev => ({ ...prev, search: value || undefined }))}
              onChange={(e) => {
                const val = e.target.value;
                setFilters(prev => ({ ...prev, search: val || undefined }));
              }}
              allowClear
              enterButton
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Type"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters(prev => ({ ...prev, type: value || undefined }))}
            >
              <Option value="percentage">Pourcentage</Option>
              <Option value="fixed_amount">Montant fixe</Option>
              <Option value="free_service">Service gratuit</Option>
              <Option value="bundle">Bundle</Option>
              <Option value="FGA1">FGA1 (À la demande)</Option>
              <Option value="FGA2">FGA2 (Confort à la demande)</Option>
              <Option value="3splits_50%">3splits_50% (Abonnement annuel ≥ 3 splits)</Option>
              <Option value="annual_semestriel">Annuel-Semestriel (Offres annuelles & semestrielles)</Option>
              <Option value="quarterly">Annuel-Trimestriel (Offres annuelles trimestrielles)</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Statut"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value || undefined }))}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="upcoming">À venir</Option>
              <Option value="expired">Expirée</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          dataSource={promotions || []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (page, pageSize) => {
              loadPromotions(page, pageSize);
            },
          }}
          locale={{
            emptyText: 'Aucune promotion trouvée'
          }}
        />
      </Card>

      {/* Modal de création/édition */}
      <Modal
        title={editMode ? 'Modifier la promotion' : 'Nouvelle promotion'}
        open={openDialog}
        onCancel={() => setOpenDialog(false)}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Nom"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Code promo"
                rules={[{ required: true, message: 'Le code est requis' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Le type est requis' }]}
              >
                <Select>
                  <Option value="percentage">Pourcentage</Option>
                  <Option value="fixed_amount">Montant fixe</Option>
                  <Option value="free_service">Service gratuit</Option>
                  <Option value="bundle">Bundle</Option>
                  <Option value="FGA1">FGA1 (À la demande)</Option>
                  <Option value="FGA2">FGA2 (Confort à la demande)</Option>
                  <Option value="3splits_50%">3splits_50% (Abonnement annuel ≥ 3 splits)</Option>
                  <Option value="annual_semestriel">Annuel-Semestriel (Offres annuelles &amp; semestrielles)</Option>
                  <Option value="quarterly">Annuel-Trimestriel (Offres annuelles trimestrielles)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="value"
                label="Valeur"
                rules={[{ required: true, message: 'La valeur est requise' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="Date de début"
                rules={[{ required: true, message: 'La date de début est requise' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="end_date"
                label="Date de fin"
                rules={[{ required: true, message: 'La date de fin est requise' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="usage_limit"
                label="Limite d'utilisation"
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="target"
                label="Cible"
              >
                <Select>
                  <Option value="all">Tous</Option>
                  <Option value="customers">Clients</Option>
                  <Option value="technicians">Techniciens</Option>
                  <Option value="new_users">Nouveaux utilisateurs</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal de visualisation */}
      <Modal
        title="Détails de la promotion"
        open={openViewDialog}
        onCancel={() => setOpenViewDialog(false)}
        footer={[
          <Button key="close" onClick={() => setOpenViewDialog(false)}>
            Fermer
          </Button>
        ]}
        width={600}
      >
        {selectedPromotion && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <strong>Nom:</strong> {selectedPromotion.name}
              </Col>
              <Col span={12}>
                <strong>Code:</strong> {selectedPromotion.code}
              </Col>
            </Row>
            <br />
            <Row gutter={16}>
              <Col span={12}>
                <strong>Type:</strong> {getTypeText(selectedPromotion.type)}
              </Col>
              <Col span={12}>
                <strong>Valeur:</strong> {(selectedPromotion.type === 'percentage' || selectedPromotion.type === '3splits_50%' || selectedPromotion.type === 'FGA3' || selectedPromotion.type === 'fga3') ? `${selectedPromotion.value}%` : (selectedPromotion.type === 'annual_semestriel' || selectedPromotion.type === 'annuel_semestriel' || selectedPromotion.type === 'quarterly' || selectedPromotion.type === 'trimestriel') ? (selectedPromotion.value <= 100 ? `${selectedPromotion.value}%` : `${selectedPromotion.value} FCFA`) : `${selectedPromotion.value} FCFA`}
              </Col>
            </Row>
            <br />
            <Row gutter={16}>
              <Col span={12}>
                <strong>Date de début:</strong> {dayjs(selectedPromotion.start_date).format('DD/MM/YYYY')}
              </Col>
              <Col span={12}>
                <strong>Date de fin:</strong> {dayjs(selectedPromotion.end_date).format('DD/MM/YYYY')}
              </Col>
            </Row>
            <br />
            <Row gutter={16}>
              <Col span={24}>
                <strong>Description:</strong> {selectedPromotion.description || 'Aucune description'}
              </Col>
            </Row>
            <br />
            <Row gutter={16}>
              <Col span={12}>
                <strong>Statut:</strong> <Tag color={getStatusColor(selectedPromotion)}>{getStatusText(selectedPromotion)}</Tag>
              </Col>
              <Col span={12}>
                <strong>Utilisations:</strong> {selectedPromotion.used_count || 0} / {selectedPromotion.usage_limit || '∞'}
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PromotionsPage;